import React, { useState, useEffect, useMemo } from 'react';
import Cookies from 'js-cookie';
import axios from 'axios';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import Topbar from '../layouts/Topbar';
import Sidebar from '../layouts/Sidebar';
import API_BASE_URL from '../../../config/api'
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    BarChart,
    Bar,
    Legend,
    LineChart,
    Line
} from 'recharts';
import {
    MdPeople,
    MdAssignment,
    MdTrendingUp,
    MdStar,
    MdCalendarToday,
    MdMoreVert
} from 'react-icons/md';
import './Dashboard.css';
import Select from 'react-select';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const quarterOptions = [
    { value: 'Q1', label: 'Quarter 1' },
    { value: 'Q2', label: 'Quarter 2' },
    { value: 'Q3', label: 'Quarter 3' },
    { value: 'Q4', label: 'Quarter 4' }
];

const Analytics = () => {
    const [courseCenters, setCourseCenters] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [selectedCourseCenter, setSelectedCourseCenter] = useState(null);
    const [selectedDepartment, setSelectedDepartment] = useState(null);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedQuarter, setSelectedQuarter] = useState(null);
    const [loading, setLoading] = useState(true);
    const [analytics, setAnalytics] = useState(null);
    const baseUrl = API_BASE_URL;
    const COLORS = ['#4F46E5', '#818CF8', '#A5B4FC', '#C7D2FE'];

    // Fetch filter options
    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const token = Cookies.get('token');
                const [ccRes, depRes] = await Promise.all([
                    axios.get(`${baseUrl}/api/course-centers/`, { headers: { 'Authorization': `Bearer ${token}` } }),
                    axios.get(`${baseUrl}/api/departments/`, { headers: { 'Authorization': `Bearer ${token}` } })
                ]);
                setCourseCenters(ccRes.data);
                setDepartments(depRes.data);
            } catch (err) {
                setCourseCenters([]);
                setDepartments([]);
            }
        };
        fetchOptions();
    }, []);

    // Fetch analytics data
    useEffect(() => {
        const fetchAnalytics = async () => {
            setLoading(true);
            try {
                const token = Cookies.get('token');
                const params = {
                    filtering_method: 'filter',
                    course_center: selectedCourseCenter?.id,
                    department: selectedDepartment?.id,
                    year: selectedYear,
                    quarter: selectedQuarter?.value
                };
                // Remove undefined/null params
                Object.keys(params).forEach(key => (params[key] == null) && delete params[key]);
                const response = await axios.get(`${baseUrl}/api/performance-analytics`, {
                    headers: { 'Authorization': `Bearer ${token}` },
                    params
                });
                setAnalytics(response.data);
            } catch (err) {
                setAnalytics(null);
            } finally {
                setLoading(false);
            }
        };
        fetchAnalytics();
    }, [selectedCourseCenter, selectedDepartment, selectedYear, selectedQuarter]);

    // Chart data
    const quarterlyTrendsData = useMemo(() => {
        if (!analytics?.quarterly_trends) return [];
        return Object.entries(analytics.quarterly_trends).map(([quarter, stats]) => ({
            quarter,
            ...stats
        }));
    }, [analytics]);

    // Stat values
    const stats = analytics?.overall_statistics || {};
    
    // Helper function to format numbers with 2 decimal places if decimal, otherwise as integer
    const formatNumber = (value) => {
        if (typeof value === 'number') {
            return Number.isInteger(value) ? value : value.toFixed(2);
        }
        return value;
    };

    // Pie chart data
    const statusDistribution = analytics?.status_distribution || [];
    const totalGoals = useMemo(() => statusDistribution.reduce((sum, item) => sum + (item.count || 0), 0), [statusDistribution]);

    // Prepare course center statistics data for bar chart
    const courseCenterStatsData = useMemo(() => {
        if (!analytics?.course_center_statistics) return [];
        return Object.entries(analytics.course_center_statistics).map(([name, stats]) => ({
            name,
            ...stats
        }));
    }, [analytics]);

    // Prepare department statistics data for line chart
    const departmentStatsData = useMemo(() => {
        if (!analytics?.department_statistics) return [];
        return Object.entries(analytics.department_statistics).map(([name, stats]) => ({
            name,
            ...stats
        }));
    }, [analytics]);

    // Year options
    const yearOptions = useMemo(() => {
        const current = new Date().getFullYear();
        const start = 2025;
        const end = current + 1; // Include the following year
        const count = Math.max(1, end - start + 1);
        return Array.from({ length: count }, (_, i) => start + i);
    }, []);
    const yearSelectOptions = useMemo(() => yearOptions.map(y => ({ value: y, label: String(y) })), [yearOptions]);

    // StatCard component (matches Dashboard.jsx)
    const StatCard = ({ icon: Icon, title, value, loading, showArrow, linkTo }) => (
        <div className="stat-card">
            {loading ? (
                <Skeleton height={100} />
            ) : (
                <>
                    <div className="stat-icon">
                        <Icon />
                    </div>
                    <div className="stat-content">
                        <h3>{title}</h3>
                        <div className="stat-value">{typeof value === 'number' ? value : value}</div>
                    </div>
                </>
            )}
        </div>
    );

    // ChartCard component (matches Dashboard.jsx)
    const ChartCard = ({ title, children, loading }) => (
        <div className="chart-card">
            {loading ? (
                <Skeleton height={300} />
            ) : (
                <>
                    <div className="chart-header">
                        <h3>{title}</h3>
                    </div>
                    <div className="chart-content">
                        {children}
                    </div>
                </>
            )}
        </div>
    );

    // Prepare react-select options
    const costCenterOptions = useMemo(() => [
        { value: '', label: 'All Cost Centers' },
        ...courseCenters.map(cc => ({ value: cc.id, label: cc.name, raw: cc }))
    ], [courseCenters]);
    const departmentOptions = useMemo(() => [
        { value: '', label: 'All Departments' },
        ...departments.map(dep => ({ value: dep.id, label: dep.name, raw: dep }))
    ], [departments]);

    // Generate PDF Report function
    const generatePDFReport = () => {
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 20;
        const contentWidth = pageWidth - (2 * margin);
        let yPosition = 20;

        // Add diagonal watermark
        pdf.setTextColor(200, 200, 200); // Light gray color
        pdf.setFontSize(24);
        pdf.setFont('helvetica', 'bold');
        
        // Create single diagonal watermark from bottom left to top right
        const watermarkText = '';
        
        // Position watermark diagonally across the page
        // Start from bottom left area and extend to top right
        const startX = margin + 10;
        const startY = pageHeight - margin - 10;
        const endX = pageWidth - margin - 10;
        const endY = margin + 10;
        
        // Calculate diagonal positioning for single text
        const diagonalX = startX + (endX - startX) * 0.3; // Position at 30% along diagonal
        const diagonalY = startY - (startY - endY) * 0.3; // Position at 30% along diagonal
        
        // Draw single watermark text
        pdf.text(watermarkText, diagonalX, diagonalY, { align: 'center' });
        
        // Reset text color for normal content
        pdf.setTextColor(0, 0, 0);

        // Add title
        pdf.setFontSize(20);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Performance Analytics Report', pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 15;

        // Add date
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'normal');
        const currentDate = new Date().toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
        pdf.text(`Generated on: ${currentDate}`, pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 20;

        // Add filters section
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Applied Filters:', margin, yPosition);
        yPosition += 10;

        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        
        const filters = [];
        if (selectedCourseCenter) {
            filters.push(`Cost Center: ${selectedCourseCenter.name}`);
        }
        if (selectedDepartment) {
            filters.push(`Department: ${selectedDepartment.name}`);
        }
        if (selectedYear) {
            filters.push(`Year: ${selectedYear}`);
        }
        if (selectedQuarter) {
            filters.push(`Quarter: ${selectedQuarter.label}`);
        }
        if (filters.length === 0) {
            filters.push('All filters applied (no specific selection)');
        }

        filters.forEach(filter => {
            pdf.text(filter, margin + 5, yPosition);
            yPosition += 6;
        });
        yPosition += 15;

        // Add Overall Statistics table
        if (analytics?.overall_statistics) {
            pdf.setFontSize(14);
            pdf.setFont('helvetica', 'bold');
            pdf.text('Overall Statistics', margin, yPosition);
            yPosition += 10;

                         const statsData = [
                 ['Metric', 'Value'],
                 ['Total Employees', formatNumber(stats.total_employees || 0)],
                 ['Average Performance', `${formatNumber(stats.average_progress || 0)}%`],
                 ['Completion Rate', `${formatNumber(stats.completion_rate || 0)}%`],
                 ['Completed Goals', `${formatNumber(stats.completed_goals || 0)} / ${formatNumber(stats.total_goals || 0)}`]
             ];

            autoTable(pdf, {
                startY: yPosition,
                head: [statsData[0]],
                body: statsData.slice(1),
                theme: 'grid',
                styles: { fontSize: 10 },
                headStyles: { fillColor: [79, 70, 229] },
                margin: { left: margin, right: margin }
            });

            yPosition = pdf.lastAutoTable.finalY + 15;
        }

        // Add Quarterly Trends table
        if (quarterlyTrendsData.length > 0) {
            pdf.setFontSize(14);
            pdf.setFont('helvetica', 'bold');
            pdf.text('Quarterly Trends', margin, yPosition);
            yPosition += 10;

            const quarterlyHeaders = ['Quarter', 'Completion Rate (%)', 'Total Goals', 'Completed Goals'];
                         const quarterlyBody = quarterlyTrendsData.map(item => [
                 item.quarter,
                 `${formatNumber(Number(item.completion_rate || 0))}%`,
                 formatNumber(item.total_goals || 0),
                 formatNumber(item.completed_goals || 0)
             ]);

            autoTable(pdf, {
                startY: yPosition,
                head: [quarterlyHeaders],
                body: quarterlyBody,
                theme: 'grid',
                styles: { fontSize: 9 },
                headStyles: { fillColor: [79, 70, 229] },
                margin: { left: margin, right: margin }
            });

            yPosition = pdf.lastAutoTable.finalY + 15;
        }

        // Add Course Center Statistics table
        if (courseCenterStatsData.length > 0) {
            pdf.setFontSize(14);
            pdf.setFont('helvetica', 'bold');
            pdf.text('Cost Center Performance', margin, yPosition);
            yPosition += 10;

            const ccHeaders = ['Cost Center', 'Avg Performance (%)', 'Total Goals', 'Completed Goals'];
                         const ccBody = courseCenterStatsData.map(item => [
                 item.name,
                 `${formatNumber(Number(item.average_progress || 0))}%`,
                 formatNumber(item.total_goals || 0),
                 formatNumber(item.completed_goals || 0)
             ]);

            autoTable(pdf, {
                startY: yPosition,
                head: [ccHeaders],
                body: ccBody,
                theme: 'grid',
                styles: { fontSize: 9 },
                headStyles: { fillColor: [79, 70, 229] },
                margin: { left: margin, right: margin }
            });

            yPosition = pdf.lastAutoTable.finalY + 15;
        }

        // Add Department Statistics table
        if (departmentStatsData.length > 0) {
            pdf.setFontSize(14);
            pdf.setFont('helvetica', 'bold');
            pdf.text('Department Performance', margin, yPosition);
            yPosition += 10;

            const deptHeaders = ['Department', 'Avg Performance (%)', 'Total Goals', 'Completed Goals'];
                         const deptBody = departmentStatsData.map(item => [
                 item.name,
                 `${formatNumber(Number(item.average_progress || 0))}%`,
                 formatNumber(item.total_goals || 0),
                 formatNumber(item.completed_goals || 0)
             ]);

            autoTable(pdf, {
                startY: yPosition,
                head: [deptHeaders],
                body: deptBody,
                theme: 'grid',
                styles: { fontSize: 9 },
                headStyles: { fillColor: [79, 70, 229] },
                margin: { left: margin, right: margin }
            });

            yPosition = pdf.lastAutoTable.finalY + 15;
        }

        // Add Status Distribution table
        if (statusDistribution.length > 0) {
            pdf.setFontSize(14);
            pdf.setFont('helvetica', 'bold');
            pdf.text('Goal Status Distribution', margin, yPosition);
            yPosition += 10;

            const statusHeaders = ['Status', 'Count', 'Percentage (%)'];
                         const statusBody = statusDistribution.map(item => [
                 item.status || 'Unknown',
                 formatNumber(item.count || 0),
                 `${totalGoals > 0 ? formatNumber((item.count || 0) / totalGoals * 100) : 0}%`
             ]);

            autoTable(pdf, {
                startY: yPosition,
                head: [statusHeaders],
                body: statusBody,
                theme: 'grid',
                styles: { fontSize: 9 },
                headStyles: { fillColor: [79, 70, 229] },
                margin: { left: margin, right: margin }
            });
        }

        // Save the PDF
        const dateStr = new Date().toISOString().slice(0, 10);
        pdf.save(`Performance_Analytics_Report_${dateStr}.pdf`);
    };

    return (
        <>
        <Topbar/>
        <Sidebar/>
        <div className="dashboard-container">
            <div className="dashboard-header analytics-header-flex">
                <div>
                    <h1 style={{ margin: 0 }}>Analytics</h1>
                    <p style={{ margin: 0 }}>Filter and analyze performance data</p>
                </div>
                <button
                    style={{
                        background: '#4F46E5',
                        color: 'white',
                        border: 'none',
                        borderRadius: 8,
                        padding: '8px 22px',
                        fontWeight: 600,
                        fontSize: 15,
                        cursor: 'pointer',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                        transition: 'background 0.2s',
                        marginLeft: 16
                    }}
                    onClick={() => generatePDFReport()}
                >
                    Generate Report
                </button>
                <div className="analytics-filter-row">
                    <div className="analytics-filter">
                        <label htmlFor="cost-center-select">Cost Center</label>
                        <Select
                            inputId="cost-center-select"
                            classNamePrefix="analytics-select"
                            options={costCenterOptions}
                            value={selectedCourseCenter ? { value: selectedCourseCenter.id, label: selectedCourseCenter.name } : costCenterOptions[0]}
                            onChange={option => {
                                if (!option || !option.value) {
                                    setSelectedCourseCenter(null);
                                } else {
                                    const cc = courseCenters.find(c => c.id === option.value);
                                    setSelectedCourseCenter(cc || null);
                                }
                            }}
                            isClearable
                            isSearchable
                            placeholder="All Cost Centers"
                        />
                    </div>
                    <div className="analytics-filter">
                        <label htmlFor="department-select">Department</label>
                        <Select
                            inputId="department-select"
                            classNamePrefix="analytics-select"
                            options={departmentOptions}
                            value={selectedDepartment ? { value: selectedDepartment.id, label: selectedDepartment.name } : departmentOptions[0]}
                            onChange={option => {
                                if (!option || !option.value) {
                                    setSelectedDepartment(null);
                                } else {
                                    const dep = departments.find(d => d.id === option.value);
                                    setSelectedDepartment(dep || null);
                                }
                            }}
                            isClearable
                            isSearchable
                            placeholder="All Departments"
                        />
                    </div>
                    <div className="analytics-filter">
                        <label htmlFor="year-select">Year</label>
                        <Select
                            inputId="year-select"
                            classNamePrefix="analytics-select"
                            options={yearSelectOptions}
                            value={{ value: selectedYear, label: String(selectedYear) }}
                            onChange={option => {
                                if (option && option.value) {
                                    setSelectedYear(option.value);
                                }
                            }}
                            isSearchable
                            menuPlacement="auto"
                        />
                    </div>
                </div>
            </div>
            <div id="analytics-report-main-content">
                <div className="stats-grid">
                    <StatCard
                        icon={MdPeople}
                        title="Total Employees"
                        value={formatNumber(stats.total_employees || 0)}
                        loading={loading}
                    />
                    <StatCard
                        icon={MdTrendingUp}
                        title="Avg Performance"
                        value={typeof stats.average_progress === 'number' ? `${formatNumber(stats.average_progress)}%` : '0%'}
                        loading={loading}
                    />
                    <StatCard
                        icon={MdTrendingUp}
                        title="Completion Rate"
                        value={typeof stats.completion_rate === 'number' ? `${formatNumber(stats.completion_rate)}%` : '0%'}
                        loading={loading}
                    />
                    <StatCard
                        icon={MdStar}
                        title="Completed Goals"
                        value={`${formatNumber(stats.completed_goals || 0)} / ${formatNumber(stats.total_goals || 0)}`}
                        loading={loading}
                    />
                    
                </div>

                <div style={{ marginBottom: '2rem' }}>
                    <ChartCard
                        title="Quarterly Goal Completion Rate"
                        loading={loading}
                        
                    >
                        <ResponsiveContainer width="100%" height={300}>
                            <AreaChart data={quarterlyTrendsData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="colorQuarterly" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#4F46E5" stopOpacity={0.8}/>
                                        <stop offset="95%" stopColor="#4F46E5" stopOpacity={0}/>
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="quarter" />
                                                                 <YAxis domain={[0, 100]} tickFormatter={(v) => `${formatNumber(Number(v))}%`} />
                                 <Tooltip formatter={(value) => `${formatNumber(Number(value))}%`} />
                                <Area 
                                    type="monotone" 
                                    dataKey="completion_rate" 
                                    stroke="#4F46E5" 
                                    fillOpacity={1} 
                                    fill="url(#colorQuarterly)" 
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </div>
                <div style={{ marginBottom: '2rem' }}>
                    <ChartCard title="Cost Center Avg Perfomance" loading={loading}>
                        <div style={{ width: '100%', overflowX: 'auto' }}>
                            <div style={{ minWidth: Math.max(600, courseCenterStatsData.length * 120) }}>
                                <ResponsiveContainer width="100%" height={350}>
                                    <BarChart data={courseCenterStatsData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="name" />
                                        <YAxis domain={[0, 100]} tickFormatter={v => `${formatNumber(Number(v))}%`} />
                                        <Tooltip formatter={value => `${formatNumber(Number(value))}%`} />
                                        <Legend />
                                        <Bar dataKey="average_progress" name="Avg Perfomance (%)" fill="#818CF8" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </ChartCard>
                </div>
                <div>
                    <ChartCard title="Department Avg Perfomance" loading={loading}>
                        <div style={{ width: '100%', overflowX: 'auto' }}>
                            <div style={{ minWidth: Math.max(600, departmentStatsData.length * 120) }}>
                                <ResponsiveContainer width="100%" height={350}>
                                    <LineChart data={departmentStatsData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="name" />
                                        <YAxis domain={[0, 100]} tickFormatter={v => `${formatNumber(Number(v))}%`} />
                                        <Tooltip formatter={value => `${formatNumber(Number(value))}%`} />
                                        <Legend />
                                        <Line type="monotone" dataKey="average_progress" name="Avg Perfomance (%)" stroke="#4F46E5" strokeWidth={3} dot={{ r: 5 }} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </ChartCard>
                </div>
            </div>
        </div>
        </>
    );
}

export default Analytics;
